import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { TokenService } from '../service/token.service';
import { ProjectSaleInfo } from '../model/projectsale.model';
import { ChannelTypeInfo } from '../model/ChannelType.model';
import { ChannelMappingInfo } from '../model/ChannelMapping.model';
import { ChartOptions, ChartType, ChartDataSets } from 'chart.js';
import { Label, Color, BaseChartDirective } from 'ng2-charts';

@Component({
  selector: 'app-lead-summary-by-channel',
  templateUrl: './lead-summary-by-channel.component.html',
  styleUrls: ['./lead-summary-by-channel.component.css']
})
export class LeadSummaryByChannelComponent implements OnInit {
  baseurl: string;

  EmpCode: string | null = null;

  ChannelTypeList: ChannelTypeInfo[] = [];
  ChannelMappingDList: ChannelMappingInfo[] = [];
  ChannelMappingCList: ChannelMappingInfo[] = [];
  ChannelTypeMasterList: ChannelTypeInfo[] = [];
  ChannelMappingDMasterList: ChannelMappingInfo[] = [];
  ChannelMappingCMasterList: ChannelMappingInfo[] = [];
  isDealerChannel: boolean = false;
  isLoading: boolean = false;
  loading: boolean = false;

  ProjectSaleChannelTypeCode: string | null = null;
  ProjectSaleChannelMappingCode: string | null = null;

  ProjectSaleList: ProjectSaleInfo[] = [];

  ChartMode = "RING"; // BAR or RING

  // Chart configuration
  doughnutChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      position: 'bottom',
      labels: {
        fontSize: 10,
        boxWidth: 10,
        padding: 3,
        generateLabels: function(chart) {
          const data = chart.data;
          if (data.labels && data.datasets && data.datasets.length) {
            return data.labels.map(function(label, i) {
              const meta = chart.getDatasetMeta(0);
              const ds = data.datasets[0];
              const arc = meta.data[i];
              const value = ds.data[i] as number;
              
              // Get the default color
              const arcOpts = chart.options.elements.arc;
              const fill = (ds.backgroundColor && ds.backgroundColor[i]) ? ds.backgroundColor[i] : arcOpts.backgroundColor;
              const stroke = (ds.borderColor && ds.borderColor[i]) ? ds.borderColor[i] : arcOpts.borderColor;
              
              return {
                text: label + ': ' + value,
                fillStyle: fill,
                strokeStyle: stroke,
                lineWidth: typeof arcOpts.borderWidth === 'number' ? arcOpts.borderWidth : 1,
                hidden: isNaN(ds.data[i] as number) || meta.data[i].hidden,
                index: i
              };
            });
          }
          return [];
        }
      }
    },
    tooltips: {
      enabled: true,
      mode: 'single',
      intersect: true,
      callbacks: {
        label: function(tooltipItem, data) {
          if (tooltipItem && data && data.datasets) {
            const dataset = data.datasets[tooltipItem.datasetIndex];
            if (dataset && dataset.data) {
              const total = (dataset.data as number[]).reduce((previousValue, currentValue) => {
                return previousValue + currentValue;
              }, 0);
              const currentValue = dataset.data[tooltipItem.index] as number;
              const percentage = Math.floor((currentValue/total) * 100);
              return (data.labels ? data.labels[tooltipItem.index] : '') + ': ' + currentValue + ' (' + percentage + '%)';
            }
          }
          return '';
        }
      }
    },
    cutoutPercentage: 60 // Moderate hole size
  };

  barChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
          stepSize: 1,
          precision: 0,
          maxTicksLimit: 10
        }
      }],
      xAxes: [{
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 0
        },
        gridLines: {
          display: false
        }
      }]
    },
    legend: {
      display: false
    },
    tooltips: {
      enabled: true,
      callbacks: {
        label: function(tooltipItem) {
          if (tooltipItem && tooltipItem.yLabel) {
            return 'Count: ' + tooltipItem.yLabel;
          }
          return '';
        }
      }
    },
    animation: {
      duration: 0 // Disable animation to prevent flickering
    },
    responsiveAnimationDuration: 0 // Disable responsive animation
  };

  doughnutChartColors: Color[] = [
    {
      backgroundColor: [
        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
        '#FF9F40', '#8AC249', '#EA526F', '#23B5D3', '#279AF1'
      ]
    }
  ];

  barChartColors: Color[] = [
    {
      backgroundColor: '#36A2EB',
      borderColor: '#1985AC',
      borderWidth: 1
    }
  ];

  // Add period selection properties
  periodSearch: string = "YEAR"; // YEAR, QUARTER, MONTH
  yearSearch: number | null = null;
  quarterSearch: string | null = null;
  monthSearch: any | null = null;

  // Period options
  yearOptions: number[] = [];
  quarterOptions: string[] = ["Q1", "Q2", "Q3", "Q4"];
  monthOptions: any[] = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
  ];

  constructor(
    private httpClient: HttpClient,
    private TokenService: TokenService
  ) {
    this.baseurl = environment.base_api;

    this.EmpCode = localStorage.getItem("EmpCode");

    // Initialize year list
    const currentYear = new Date().getFullYear();
    this.yearOptions = Array.from({length: 5}, (_, i) => currentYear - i);
  }

  async ngOnInit(): Promise<void> {
    this.loadChannelTypes();
    this.loadChannelMapping('D');
    this.loadChannelMapping('C');
  }

  async loadChannelTypes() {
    try {
      const auth_token = await this.TokenService.RefreshToken();
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth_token}`
        })
      };

      const objectjson = {
        EmpCode: this.EmpCode,
      };

      const response = await this.httpClient.post<any>(
        this.baseurl + "Channel/GetChannelTypeList",
        JSON.stringify(objectjson),
        httpOptions
      ).toPromise();

      this.ChannelTypeList = response;
      this.ChannelTypeMasterList = response;
    } catch (error) {
      console.error('Error loading channel types:', error);
    }
  }

  async loadChannelMapping(type: string) {
    try {
      const auth_token = await this.TokenService.RefreshToken();
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth_token}`
        })
      };

      const objectjson = {
        ChannelTypeCode: this.ProjectSaleChannelTypeCode,
        ChannelMappingType: type,
        ChannelMappingFlagActive: "Y"
      };

      const response = await this.httpClient.post<any>(
        this.baseurl + "Channel/GetChannelMappingList",
        JSON.stringify(objectjson),
        httpOptions
      ).toPromise();

      if (type === 'D') {
        //this.ChannelMappingDList = response;
        this.ChannelMappingDMasterList = response;
      } else {
        //this.ChannelMappingCList = response;
        this.ChannelMappingCMasterList = response;
      }
    } catch (error) {
      console.error('Error loading channel mapping:', error);
    }
  }

  async reloadChannelMapping(type: string){

    try {
      // Check if channel type code is selected
      if (!this.ProjectSaleChannelTypeCode) {
        this.ChannelMappingDList = [];
        this.ChannelMappingCList = [];
        return;
      }
  
      // Filter based on the type parameter and selected channel type
      if (type === 'D') {
        // Filter Dealer channel mappings
        this.ChannelMappingDList = this.ChannelMappingDMasterList.filter(item => 
          item.ChannelMappingFlagActive === 'Y' && 
          item.ChannelTypeCode === this.ProjectSaleChannelTypeCode
        );
      } else {
        // Filter Channel channel mappings
        this.ChannelMappingCList = this.ChannelMappingCMasterList.filter(item => 
          item.ChannelMappingFlagActive === 'Y' && 
          item.ChannelTypeCode === this.ProjectSaleChannelTypeCode
        );
      }
    } catch (error) {
      console.error('Error reloading channel mapping:', error);
    }

  }

  async onChannelTypeChange(event: any) {
    const selectedValue = event.target.value;
    const selectedChannel = this.ChannelTypeList.find(x => x.ChannelTypeCode === selectedValue);

    this.ProjectSaleChannelMappingCode = null;

    if (selectedChannel && selectedChannel.ChannelTypeFlagDealer === 'Y') {
      this.isDealerChannel = true;
      await this.reloadChannelMapping('D');
    } else {
      this.isDealerChannel = false;
      await this.reloadChannelMapping('C');
    }

    await this.refreshCharts();
  }

  async onChannelSubTypeChange(event: any){



    await this.refreshCharts();
  }


  async onPeriodChange() {
    //this.yearSearch = null;
    this.quarterSearch = null;
    this.monthSearch = null;


    await this.refreshCharts();
  }

  async onYearChange() {
    await this.refreshCharts();
  }

  async onQuarterChange() {
    this.monthSearch = null;
    await this.refreshCharts();
  }

  async onMonthChange() {
    this.quarterSearch = null;
    await this.refreshCharts();
  }

  async refreshCharts() {

     this.loading = true;

    if(this.quarterSearch == null && this.monthSearch == null){
      this.ChartMode = "RING";
    }else{
      this.ChartMode = "BAR";
    }

    try {
      const auth_token = await this.TokenService.RefreshToken();
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth_token}`
        })
      };

      const objectjson = {
        // Period: this.periodSearch,
        // Year: this.yearSearch,
        // Quarter: this.quarterSearch,
        // Month: this.monthSearch,
        // ChannelTypeCode: this.ProjectSaleChannelTypeCode,
        EmpCode: this.EmpCode
      };

      const response = await this.httpClient.post<any>(
        this.baseurl + "Dashboard/GetLeadSummaryByChannel",
        JSON.stringify(objectjson),
        httpOptions
      ).toPromise();

      // Handle the response data
      this.ProjectSaleList = response;

      console.log(this.ProjectSaleList)
    } catch (error) {
      console.error('Error loading channel data:', error);
    } finally {
      this.loading = false;
    }
  }

  // Filter ProjectSaleList based on current filters
  getFilteredProjectSaleList(): ProjectSaleInfo[] {
    if (!this.ProjectSaleList || this.ProjectSaleList.length === 0) {
      return [];
    }

    return this.ProjectSaleList.filter(item => {
      // Filter by year
      if (this.yearSearch !== null) {
        const itemYear = new Date(item.ProjectSaleCreateDate).getFullYear();
        if (itemYear !== this.yearSearch) {
          return false;
        }
      }

      // Filter by quarter if selected
      if (this.quarterSearch) {
        const month = new Date(item.ProjectSaleCreateDate).getMonth() + 1;
        const quarter = Math.ceil(month / 3);
        if ('Q' + quarter !== this.quarterSearch) {
          return false;
        }
      }

      // Filter by month if selected
      if (this.monthSearch) {
        const month = new Date(item.ProjectSaleCreateDate).getMonth() + 1;
        if (month !== parseInt(this.monthSearch)) {
          return false;
        }
      }

      // Filter by channel type if selected
      if (this.ProjectSaleChannelTypeCode && item.ProjectSaleChannelTypeCode !== this.ProjectSaleChannelTypeCode) {
        return false;
      }

      // Filter by channel mapping if selected
      if (this.ProjectSaleChannelMappingCode && item.ProjectSaleChannelMappingCode !== this.ProjectSaleChannelMappingCode) {
        return false;
      }

      return true;
    });
  }

  // Get unique years from filtered data
  getUniqueYears(): number[] {
    const filteredData = this.ProjectSaleList;
    if (filteredData.length === 0) {
      return [new Date().getFullYear()]; // Default to current year if no data
    }

    const years = new Set<number>();
    filteredData.forEach(item => {
      const year = new Date(item.ProjectSaleCreateDate).getFullYear();
      years.add(year);
    });

    return Array.from(years).sort((a, b) => a - b);
  }

  // Get unique quarters from filtered data
  getUniqueQuarters(): number[] {
    // When a year is selected, always show all quarters
    if (this.yearSearch !== null) {
      return [1, 2, 3, 4];
    }
    
    const filteredData = this.getFilteredProjectSaleList();
    if (filteredData.length === 0) {
      return [1, 2, 3, 4]; // Default to all quarters if no data
    }

    const quarters = new Set<number>();
    filteredData.forEach(item => {
      const month = new Date(item.ProjectSaleCreateDate).getMonth() + 1;
      const quarter = Math.ceil(month / 3);
      quarters.add(quarter);
    });

    return Array.from(quarters).sort((a, b) => a - b);
  }

  // Get unique year-quarter combinations from filtered data
  getUniqueYearQuarters(): {year: number, quarter: number}[] {
    const filteredData = this.ProjectSaleList;
    if (filteredData.length === 0) {
      const currentYear = new Date().getFullYear();
      return [
        {year: currentYear, quarter: 1},
        {year: currentYear, quarter: 2},
        {year: currentYear, quarter: 3},
        {year: currentYear, quarter: 4}
      ]; // Default to all quarters of current year if no data
    }

    const yearQuarters = new Set<string>();
    const result: {year: number, quarter: number}[] = [];
    
    filteredData.forEach(item => {
      const date = new Date(item.ProjectSaleCreateDate);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const quarter = Math.ceil(month / 3);
      const key = `${year}-${quarter}`;
      
      if (!yearQuarters.has(key)) {
        yearQuarters.add(key);
        result.push({year, quarter});
      }
    });

    // Sort by year and then by quarter
    return result.sort((a, b) => {
      if (a.year !== b.year) {
        return a.year - b.year;
      }
      return a.quarter - b.quarter;
    });
  }

  // Get channel mapping labels for a specific channel type
  getChannelMappingLabels(channelType: ChannelTypeInfo): string[] {
    if (!channelType) {
      return [];
    }

    let mappingList: ChannelMappingInfo[] = [];
    
    // Check if it's a dealer channel
    if (channelType.ChannelTypeFlagDealer === 'Y') {
      // Filter dealer mappings by channel type code
      mappingList = this.ChannelMappingDMasterList.filter(
        item => item.ChannelTypeCode === channelType.ChannelTypeCode
      );
    } else {
      // Filter non-dealer mappings by channel type code
      mappingList = this.ChannelMappingCMasterList.filter(
        item => item.ChannelTypeCode === channelType.ChannelTypeCode
      );
    }

    // Return the mapping names as labels
    return mappingList.map(item => item.ChannelMappingName);
  }

  // Get data for doughnut chart (RING mode)
  getChannelMappingData(channelType: ChannelTypeInfo, quarter: number): number[] {
    if (!channelType) {
      return [];
    }

    let mappingList: ChannelMappingInfo[] = [];
    
    // Check if it's a dealer channel
    if (channelType.ChannelTypeFlagDealer === 'Y') {
      // Filter dealer mappings by channel type code
      mappingList = this.ChannelMappingDMasterList.filter(
        item => item.ChannelTypeCode === channelType.ChannelTypeCode
      );
    } else {
      // Filter non-dealer mappings by channel type code
      mappingList = this.ChannelMappingCMasterList.filter(
        item => item.ChannelTypeCode === channelType.ChannelTypeCode
      );
    }

    // Filter project sales by current filters
    let filteredData = this.getFilteredProjectSaleList().filter(item => 
      item.ProjectSaleChannelTypeCode === channelType.ChannelTypeCode
    );

    // Further filter by quarter if provided
    if (quarter) {
      filteredData = filteredData.filter(item => {
        const itemMonth = new Date(item.ProjectSaleCreateDate).getMonth() + 1;
        const itemQuarter = Math.ceil(itemMonth / 3);
        return itemQuarter === quarter;
      });
    }

    // Count records for each mapping
    return mappingList.map(mapping => {
      return filteredData.filter(item => 
        item.ProjectSaleChannelMappingCode === mapping.ChannelMappingCode
      ).length;
    });
  }

  // Get data for doughnut chart (RING mode) for a specific year and quarter
  getChannelMappingDataForYearQuarter(channelType: ChannelTypeInfo, year: number, quarter: number): number[] {
    if (!channelType) {
      return [];
    }

    let mappingList: ChannelMappingInfo[] = [];
    
    // Check if it's a dealer channel
    if (channelType.ChannelTypeFlagDealer === 'Y') {
      // Filter dealer mappings by channel type code
      mappingList = this.ChannelMappingDMasterList.filter(
        item => item.ChannelTypeCode === channelType.ChannelTypeCode
      );
    } else {
      // Filter non-dealer mappings by channel type code
      mappingList = this.ChannelMappingCMasterList.filter(
        item => item.ChannelTypeCode === channelType.ChannelTypeCode
      );
    }

    // Filter project sales by year, quarter, and channel type
    const filteredData = this.ProjectSaleList.filter(item => {
      const date = new Date(item.ProjectSaleCreateDate);
      const itemYear = date.getFullYear();
      const itemMonth = date.getMonth() + 1;
      const itemQuarter = Math.ceil(itemMonth / 3);
      
      return itemYear === year && 
             itemQuarter === quarter && 
             item.ProjectSaleChannelTypeCode === channelType.ChannelTypeCode;
    });

    // Count records for each mapping
    return mappingList.map(mapping => {
      return filteredData.filter(item => 
        item.ProjectSaleChannelMappingCode === mapping.ChannelMappingCode
      ).length;
    });
  }

  // Get data for bar chart (BAR mode)
  getBarChartData(channelType: ChannelTypeInfo): ChartDataSets[] {
    if (!channelType) {
      return [];
    }

    let mappingList: ChannelMappingInfo[] = [];
    
    // Check if it's a dealer channel
    if (channelType.ChannelTypeFlagDealer === 'Y') {
      // Filter dealer mappings by channel type code
      mappingList = this.ChannelMappingDMasterList.filter(
        item => item.ChannelTypeCode === channelType.ChannelTypeCode
      );
    } else {
      // Filter non-dealer mappings by channel type code
      mappingList = this.ChannelMappingCMasterList.filter(
        item => item.ChannelTypeCode === channelType.ChannelTypeCode
      );
    }

    // Filter project sales by current filters and channel type
    const filteredData = this.getFilteredProjectSaleList().filter(item => 
      item.ProjectSaleChannelTypeCode === channelType.ChannelTypeCode
    );

    // Count records for each mapping
    const data = mappingList.map(mapping => {
      return filteredData.filter(item => 
        item.ProjectSaleChannelMappingCode === mapping.ChannelMappingCode
      ).length;
    });

    return [{
      data: data,
      label: channelType.ChannelTypeName
    }];
  }

  // Get channel mapping labels for bar chart (with counts)
  getChannelMappingLabelsWithCount(channelType: ChannelTypeInfo): string[] {
    if (!channelType) {
      return [];
    }

    let mappingList: ChannelMappingInfo[] = [];
    
    // Check if it's a dealer channel
    if (channelType.ChannelTypeFlagDealer === 'Y') {
      // Filter dealer mappings by channel type code
      mappingList = this.ChannelMappingDMasterList.filter(
        item => item.ChannelTypeCode === channelType.ChannelTypeCode
      );
    } else {
      // Filter non-dealer mappings by channel type code
      mappingList = this.ChannelMappingCMasterList.filter(
        item => item.ChannelTypeCode === channelType.ChannelTypeCode
      );
    }

    // Filter project sales by current filters and channel type
    const filteredData = this.getFilteredProjectSaleList().filter(item => 
      item.ProjectSaleChannelTypeCode === channelType.ChannelTypeCode
    );

    // Create labels with counts
    return mappingList.map(mapping => {
      const count = filteredData.filter(item => 
        item.ProjectSaleChannelMappingCode === mapping.ChannelMappingCode
      ).length;
      
      return mapping.ChannelMappingName + ': ' + count;
    });
  }

  // Get total count for a channel type (optionally filtered by quarter)
  getTotalCountForChannelType(channelType: ChannelTypeInfo, quarter?: number): number {
    if (!channelType) {
      return 0;
    }

    // Filter by channel type
    let filteredData = this.getFilteredProjectSaleList().filter(item => 
      item.ProjectSaleChannelTypeCode === channelType.ChannelTypeCode
    );

    // Further filter by quarter if provided
    if (quarter) {
      filteredData = filteredData.filter(item => {
        const itemMonth = new Date(item.ProjectSaleCreateDate).getMonth() + 1;
        const itemQuarter = Math.ceil(itemMonth / 3);
        return itemQuarter === quarter;
      });
    }

    return filteredData.length;
  }

  // Get total count for a channel type for a specific year and quarter
  getTotalCountForChannelTypeYearQuarter(channelType: ChannelTypeInfo, year: number, quarter: number): number {
    if (!channelType) {
      return 0;
    }

    // Filter by year, quarter, and channel type
    const filteredData = this.ProjectSaleList.filter(item => {
      const date = new Date(item.ProjectSaleCreateDate);
      const itemYear = date.getFullYear();
      const itemMonth = date.getMonth() + 1;
      const itemQuarter = Math.ceil(itemMonth / 3);
      
      return itemYear === year && 
             itemQuarter === quarter && 
             item.ProjectSaleChannelTypeCode === channelType.ChannelTypeCode;
    });

    return filteredData.length;
  }

  // Helper method to get month name from month number
  getMonthName(monthNumber: string | null): string {
    if (!monthNumber) return '';
    
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const monthIndex = parseInt(monthNumber) - 1;
    return monthNames[monthIndex] || '';
  }

  // Prepare data for Google Columns Chart
  getGoogleChartData(channelType: ChannelTypeInfo): string {
    if (!channelType) {
      return JSON.stringify([]);
    }

    let mappingList: ChannelMappingInfo[] = [];
    
    // Check if it's a dealer channel
    if (channelType.ChannelTypeFlagDealer === 'Y') {
      // Filter dealer mappings by channel type code
      mappingList = this.ChannelMappingDMasterList.filter(
        item => item.ChannelTypeCode === channelType.ChannelTypeCode
      );
    } else {
      // Filter non-dealer mappings by channel type code
      mappingList = this.ChannelMappingCMasterList.filter(
        item => item.ChannelTypeCode === channelType.ChannelTypeCode
      );
    }

    // Filter project sales by current filters and channel type
    const filteredData = this.getFilteredProjectSaleList().filter(item => 
      item.ProjectSaleChannelTypeCode === channelType.ChannelTypeCode
    );

    // Prepare data for Google Chart
    const chartData = [];
    
    // First row defines the columns
    chartData.push(['Channel', 'Count']);
    
    // Add data rows
    mappingList.forEach(mapping => {
      const count = filteredData.filter(item => 
        item.ProjectSaleChannelMappingCode === mapping.ChannelMappingCode
      ).length;
      
      chartData.push([mapping.ChannelMappingName + ' (' + count + ')', count]);
    });

    return JSON.stringify(chartData);
  }

  // Get colors for Google Chart
  getGoogleChartColors(): string {
    const colors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#8AC249', '#EA526F', '#23B5D3', '#279AF1'
    ];
    return JSON.stringify(colors);
  }
}
